import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './context/AuthContext';
import { apiGet, apiPost } from './api/client';
import LvSummaryAll from './components/LvSummaryAll';
import { usePdfLvSummary } from './hooks/usePdfLvSummary';
import { formatSavedPropertyLabel } from './utils/propertyLabel';
import { fetchStoredLvPdf } from './utils/fetchStoredLvPdf';
import { detectPropertyTypeFromSummary } from './utils/propertyType';

export default function LvWorkspacePage({
  onBack,
  onSetupRequired,
  onOpenRentOrder,
  onOpenSaleOrder,
  onOpenReservationContract,
  isAdmin = false,
}) {
  const { t } = useTranslation();
  const { getToken } = useAuth();

  const [ku, setKu] = useState('');
  const [lv, setLv] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [source, setSource] = useState(null); // 'cache' | 'cuzk' | null
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [selectedHistoryKey, setSelectedHistoryKey] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [historyPdfStoragePath, setHistoryPdfStoragePath] = useState(null);
  const [sessionPdfStoragePath, setSessionPdfStoragePath] = useState(null);
  const [pdfPersistError, setPdfPersistError] = useState(null);
  const [pdfBase64, setPdfBase64] = useState('');
  const [pdfName, setPdfName] = useState('');
  const [paidRefreshConfirmed, setPaidRefreshConfirmed] = useState(false);

  const { pdfSummary, pdfPreviewError, pdfPreviewLoading } = usePdfLvSummary(pdfBase64, getToken);
  const lastPdfAutoSaveKeyRef = useRef('');

  const kuTrim = ku.trim();
  const lvTrim = lv.trim();

  const historySelectionKuLv = useMemo(() => {
    if (!selectedHistoryKey) return { kod: '', lv: '' };
    const parts = selectedHistoryKey.split('|');
    return { kod: String(parts[1] || '').trim(), lv: String(parts[2] || '').trim() };
  }, [selectedHistoryKey]);

  const loadLv = async ({ forceRefresh = false, confirmPaidRefresh = false, targetUserId = null, kodKu, cisloLv } = {}) => {
    const k = kodKu != null ? String(kodKu).trim() : kuTrim;
    const l = cisloLv != null ? String(cisloLv).trim() : lvTrim;
    if (!k || !l) return;
    if (!forceRefresh && loading) return;

    setLoading(true);
    setError(null);
    try {
      const target = targetUserId || selectedUserId || null;
      const crossAgentAdminMode = isAdmin && target && target !== undefined && target !== null;
      const refreshQuery = forceRefresh
        ? `&refresh=1${confirmPaidRefresh ? '&confirm_paid_refresh=1' : ''}`
        : '';
      const res = await apiGet(
        `/api/ownership-xml?KodKU=${encodeURIComponent(k)}&CisloLv=${encodeURIComponent(l)}${refreshQuery}${crossAgentAdminMode ? `&user_id=${encodeURIComponent(target)}` : ''}`,
        getToken,
      );
      const data = await res.json().catch(() => ({}));
      if (res.status === 403 && data.code === 'SETUP_REQUIRED' && onSetupRequired) {
        onSetupRequired();
        return;
      }
      if (!res.ok) throw new Error(data.error || t('lvWorkspace.errorFetch'));
      if (!data.xml || typeof data.xml !== 'string') throw new Error(t('lvWorkspace.errorNoXml'));
      setSummary(data.summary || null);
      setSource(data.cached ? 'cache' : 'cuzk');
    } catch (err) {
      setError(err.message || t('lvWorkspace.errorUnknown'));
    } finally {
      setLoading(false);
    }
  };

  const handleFetchFromKn = async () => {
    if (!kuTrim || !lvTrim || loading) return;
    if (isAdmin && selectedUserId) {
      setError(t('lvWorkspace.adminCacheOnlyInfo'));
      return;
    }
    if (!paidRefreshConfirmed) {
      setError(t('lvWorkspace.refreshConfirmRequired'));
      return;
    }
    await loadLv({ forceRefresh: true, confirmPaidRefresh: true });
  };

  const hasPdfSource = !!pdfBase64;
  const hasAnySource = !!summary || hasPdfSource;
  const detectedPropertyType = detectPropertyTypeFromSummary(summary || pdfSummary);
  const effectiveKu =
    kuTrim
    || historySelectionKuLv.kod
    || (pdfSummary?.kodKu ? String(pdfSummary.kodKu).replace(/\D/g, '').slice(0, 6) : '');
  const effectiveLv =
    lvTrim
    || historySelectionKuLv.lv
    || (pdfSummary?.lvCislo ? String(pdfSummary.lvCislo).replace(/\D/g, '').slice(0, 8) : '');
  const propertyRef = {
    kod_ku: effectiveKu,
    cislo_lv: effectiveLv,
    user_id: selectedUserId || null,
    pdfBase64: hasPdfSource ? pdfBase64 : undefined,
    pdfName: hasPdfSource ? pdfName : undefined,
    detected_property_type: detectedPropertyType,
    property_type_override_enabled: false,
    property_type_override: undefined,
    lv_pdf_storage_path: !hasPdfSource
      ? historyPdfStoragePath || sessionPdfStoragePath || undefined
      : sessionPdfStoragePath || undefined,
  };

  const refreshHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await apiGet(isAdmin ? '/api/properties?all=1' : '/api/properties', getToken);
      const data = await res.json().catch(() => ({}));
      if (res.ok && Array.isArray(data.properties)) {
        setHistoryItems(data.properties);
      }
    } catch {
      setHistoryItems([]);
    } finally {
      setHistoryLoading(false);
    }
  }, [getToken, isAdmin]);

  useEffect(() => {
    refreshHistory();
  }, [refreshHistory]);

  useEffect(() => {
    if (!hasPdfSource || !pdfSummary) return;
    const k = String(pdfSummary.kodKu || '').replace(/\D/g, '').slice(0, 6);
    const l = String(pdfSummary.lvCislo || '').replace(/\D/g, '').slice(0, 8);
    if (k) setKu(k);
    if (l) setLv(l);
  }, [pdfSummary, hasPdfSource]);

  useEffect(() => {
    if (!pdfBase64?.trim() || !pdfSummary || pdfPreviewLoading || pdfPreviewError) return;
    const kod = String(pdfSummary.kodKu || '').trim();
    const lvNum = String(pdfSummary.lvCislo || '').trim();
    if (!kod || !lvNum) return;

    const summarySignature = JSON.stringify(pdfSummary || {});
    const dedupeKey = `${kod}|${lvNum}|${pdfName || ''}|${pdfBase64.length}|${summarySignature}`;
    if (lastPdfAutoSaveKeyRef.current === dedupeKey) return;

    let cancelled = false;
    (async () => {
      try {
        setPdfPersistError(null);
        const res = await apiPost(
          '/api/properties',
          {
            kod_ku: kod,
            cislo_lv: lvNum,
            summary: pdfSummary,
            pdfBase64,
            source: 'pdf',
          },
          getToken,
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setPdfPersistError(data.error || t('lvWorkspace.pdfAutoSaveFailed'));
          return;
        }
        lastPdfAutoSaveKeyRef.current = dedupeKey;
        if (data.property?.lv_pdf_storage_path) {
          setSessionPdfStoragePath(String(data.property.lv_pdf_storage_path));
        }
        await refreshHistory();
      } catch (e) {
        if (!cancelled) setPdfPersistError(e.message || t('lvWorkspace.pdfAutoSaveFailed'));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    pdfBase64,
    pdfName,
    pdfSummary,
    pdfPreviewLoading,
    pdfPreviewError,
    getToken,
    t,
    refreshHistory,
  ]);

  const handleSelectHistory = async (e) => {
    const key = e.target.value;
    setSelectedHistoryKey(key);
    setPdfBase64('');
    setPdfName('');
    setSessionPdfStoragePath(null);
    setPdfPersistError(null);
    setHistoryPdfStoragePath(null);
    if (!key) {
      setSelectedUserId(null);
      return;
    }
    const [userId, k, l] = key.split('|');
    setSelectedUserId(userId || null);
    const item = historyItems.find((h) => `${h.user_id}|${h.kod_ku}|${h.cislo_lv}` === key);
    if (item?.summary?.sourcePdf === true) {
      setSummary(null);
      setSource(null);
      setError(null);
      setHistoryPdfStoragePath(item.lv_pdf_storage_path || null);
      try {
        setLoading(true);
        const { pdfBase64: b64, pdfName: name } = await fetchStoredLvPdf(getToken, {
          kod_ku: k,
          cislo_lv: l,
          user_id: isAdmin ? (userId || undefined) : undefined,
        });
        setPdfBase64(String(b64 || ''));
        setPdfName(String(name || 'LV.pdf'));
      } catch (err) {
        // Fallback to persisted summary when stored PDF is unavailable.
        setSummary(item.summary || null);
        setError(err.message || t('lvWorkspace.errorFetch'));
      } finally {
        setLoading(false);
      }
      return;
    }
    setSummary(null);
    if (k && l) {
      await loadLv({ forceRefresh: false, targetUserId: userId || null, kodKu: k, cisloLv: l });
    }
  };

  // PDF upload is intentionally disabled in XML-first mode.

  const rentCard = (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-800 mb-2">{t('lvWorkspace.groupRent')}</p>
      <div className="space-y-2">
        <button type="button" onClick={() => onOpenRentOrder && onOpenRentOrder(propertyRef)} className="w-full py-2.5 px-4 rounded-lg bg-emerald-700 text-white text-sm hover:bg-emerald-800">{t('lvWorkspace.openRentFlat')}</button>
        <button type="button" disabled className="w-full py-2.5 px-4 rounded-lg border border-emerald-200 text-emerald-700 text-sm opacity-70 cursor-not-allowed">{t('lvWorkspace.openRentHouseSoon')}</button>
      </div>
    </div>
  );

  const saleCard = (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wider text-indigo-800 mb-2">{t('lvWorkspace.groupSale')}</p>
      <div className="space-y-2">
        <button type="button" onClick={() => onOpenSaleOrder && onOpenSaleOrder(propertyRef)} className="w-full py-2.5 px-4 rounded-lg bg-indigo-700 text-white text-sm hover:bg-indigo-800">{t('lvWorkspace.openSaleFlat')}</button>
        <button type="button" onClick={() => onOpenReservationContract && onOpenReservationContract(propertyRef)} className="w-full py-2.5 px-4 rounded-lg border border-indigo-300 text-indigo-700 text-sm hover:bg-indigo-100">{t('lvWorkspace.openReservationFlat')}</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto py-6 sm:py-8 px-3 sm:px-4 w-full min-w-0">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="mb-6 text-sm text-slate-500 hover:text-slate-800 transition-colors"
        >
          ← {t('lvWorkspace.back')}
        </button>
      )}

      <div className="rounded-2xl border border-slate-200/80 bg-white shadow-lg shadow-slate-200/50 p-6 sm:p-8 space-y-5">
        <div>
          <h1 className="text-xl font-semibold text-slate-800 tracking-tight">{t('lvWorkspace.title')}</h1>
        </div>

        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider leading-relaxed">
          {t('lvWorkspace.gateCheckArchiveFirst')}
        </p>

        <div className="space-y-2">
          <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider">
            {isAdmin ? t('lvWorkspace.adminHistorySelectLabel') : t('lvWorkspace.agentHistorySelectLabel')}
          </label>
          <select
            value={selectedHistoryKey}
            onChange={handleSelectHistory}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50"
            disabled={historyLoading}
          >
            <option value="">
              {historyLoading
                ? t('lvWorkspace.loading')
                : (isAdmin ? t('lvWorkspace.adminHistorySelectPlaceholder') : t('lvWorkspace.agentHistorySelectPlaceholder'))}
            </option>
            {historyItems.map((item) => {
              const key = `${item.user_id}|${item.kod_ku}|${item.cislo_lv}`;
              const who = isAdmin ? item.user_email || item.user_id : null;
              const label = formatSavedPropertyLabel(item, t, {
                adminEmailOrIdPrefix: who || undefined,
              });
              return (
                <option key={key} value={key}>
                  {label}
                </option>
              );
            })}
          </select>
          {isAdmin ? (
            <p className="text-xs text-amber-700">{t('lvWorkspace.adminCacheOnlyInfo')}</p>
          ) : (
            <p className="text-xs text-slate-500">{t('lvWorkspace.agentHistoryHint')}</p>
          )}
        </div>

        <div className="border-t border-slate-100 pt-5 space-y-4">
          <p className="text-sm font-semibold text-slate-800">{t('lvWorkspace.gateIfNotInArchive')}</p>
          <p className="text-sm text-slate-700">{t('lvWorkspace.gateStep1EnterKuLv')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input
              type="text"
              inputMode="numeric"
              value={ku}
              onChange={(e) => {
                setSelectedHistoryKey('');
                setSelectedUserId(null);
                setPaidRefreshConfirmed(false);
                setKu(e.target.value.replace(/\D/g, '').slice(0, 6));
              }}
              placeholder={t('lvWorkspace.kuPlaceholder')}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50"
            />
            <input
              type="text"
              inputMode="numeric"
              value={lv}
              onChange={(e) => {
                setSelectedHistoryKey('');
                setSelectedUserId(null);
                setPaidRefreshConfirmed(false);
                setLv(e.target.value.replace(/\D/g, '').slice(0, 8));
              }}
              placeholder={t('lvWorkspace.lvPlaceholder')}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50/50"
            />
          </div>
          <label className="inline-flex items-start gap-2 text-sm text-slate-800">
            <input
              type="checkbox"
              checked={paidRefreshConfirmed}
              onChange={(e) => setPaidRefreshConfirmed(e.target.checked)}
              className="mt-1"
            />
            <span>{t('lvWorkspace.knFeeConsentLabel')}</span>
          </label>
          <div>
            <button
              type="button"
              onClick={handleFetchFromKn}
              disabled={
                !kuTrim
                || !lvTrim
                || loading
                || !paidRefreshConfirmed
                || (isAdmin && selectedUserId)
              }
              className="w-full sm:w-auto py-3 px-5 rounded-xl bg-slate-800 text-white font-medium text-sm hover:bg-slate-900 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? t('lvWorkspace.loading') : t('lvWorkspace.fetchFromKnButton')}
            </button>
          </div>
          {isAdmin && selectedUserId && (
            <p className="text-xs text-amber-800">{t('lvWorkspace.adminCacheOnlyInfo')}</p>
          )}
        </div>

        {/* PDF upload UX temporarily disabled (XML-first mode)
        <div className="pt-2 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
            {isAdmin ? t('lvWorkspace.adminPdfUploadSectionTitle') : t('lvWorkspace.pdfUploadSectionTitle')}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id={pdfInputId}
              type="file"
              accept="application/pdf"
              onChange={handlePdfUpload}
              className="sr-only"
            />
            <label
              htmlFor={pdfInputId}
              className="inline-flex cursor-pointer rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
            >
              {t('lvWorkspace.pdfChooseFile')}
            </label>
            <span className="text-sm text-slate-600">{pdfName || t('lvWorkspace.pdfNoFileSelected')}</span>
          </div>
          <p className="text-xs text-slate-500 mt-2">{t('lvWorkspace.pdfUploadHint')}</p>
        </div>
        */}

        {error && (
          <div className="rounded-lg bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700">
            {error}
          </div>
        )}
        {pdfPersistError && (
          <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-sm text-amber-900">
            {pdfPersistError}
          </div>
        )}

        {hasAnySource && (
          <>
            {!hasPdfSource && summary && (
              <>
                {summary.sourcePdf === true ? (
                  <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                    <span className="font-medium">{t('lvWorkspace.pdfHistoryBadge')}</span>
                  </div>
                ) : (
                  <div className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-xs ${
                    source === 'cache'
                      ? 'border border-emerald-200 bg-emerald-50 text-emerald-900'
                      : 'border border-amber-300 bg-amber-50 text-amber-900'
                  }`}>
                    <span className="text-slate-600">
                      {t('lvWorkspace.sourceLabel')}:{' '}
                      <strong>{source === 'cache' ? t('lvWorkspace.sourceCache') : t('lvWorkspace.sourceCuzk')}</strong>
                    </span>
                  </div>
                )}
                <p className="text-sm text-slate-700 font-medium leading-snug pt-1">
                  {t('lvWorkspace.reviewFetchedData')}
                </p>
                <LvSummaryAll summary={summary} />
                <p className="text-sm text-slate-600 leading-snug">
                  {t('lvWorkspace.chooseDocumentTypeHint')}
                </p>
              </>
            )}
            {/* PDF preview UI temporarily disabled (XML-first mode)
            {hasPdfSource && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
                  <span className="font-medium">{t('lvWorkspace.pdfSourceBadge')}</span>
                  <span>{t('lvWorkspace.pdfModeInfo')}</span>
                </div>
                {pdfPreviewLoading && (
                  <p className="text-sm text-slate-500">{t('lvWorkspace.pdfPreviewLoading')}</p>
                )}
                {pdfPreviewError && (
                  <div className="rounded-lg bg-amber-50 border border-amber-100 px-3 py-2 text-sm text-amber-900">
                    {pdfPreviewError}
                  </div>
                )}
                {pdfSummary && !pdfPreviewLoading && <LvSummaryAll summary={pdfSummary} />}
              </div>
            )}
            */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {saleCard}
              {rentCard}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
